import { useElements, useStripe } from "@stripe/react-stripe-js";
import Button from "components/form/Button";
import { SettingsValue } from "types/payments";
import React, { forwardRef, useImperativeHandle } from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  duplicatePaymentGet,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  storePaymentElement,
} from "store/slices/paymentsSlice";
import { useTestAttrsFactory } from "utils/testid";

interface BankPaymentProps {
  buttonText: string;
  className?: string;
  isLoading?: boolean;
  setIsLoading?: any;
  stripeBankURL: string;
  isRecurring?: boolean;
  isDisabled?: boolean;
  amount?: number;
  handleClick?: () => void;
}
const BankPaymentButton = forwardRef(
  (
    {
      buttonText,
      className,
      isLoading = false,
      setIsLoading = () => {},
      stripeBankURL,
      isRecurring = false,
      isDisabled = false,
      amount = 0,
      handleClick = () => {},
    }: BankPaymentProps,
    ref
  ) => {
    const testAttrs = useTestAttrsFactory();
    const stripe = useStripe();
    const elements = useElements();
    const userData = useUserData();
    const portalSettings = usePortalSettingsData();
    const dispatch = useAppDispatch();
    const { paymentElement, checkedDuplicatePayment } = useAppSelector(
      (state) => state.payment
    );
    const showFinalDisclosure = useShowFinalDisclosure();

    const handlePayment = async () => {
      if (!stripe || !elements) {
        return;
      }

      let payload = {};
      if (userData?.data?.accountData?.country) {
        payload = {
          ...payload,
          country: userData?.data?.accountData?.country ?? "US",
        };
      }
      if (userData?.data?.accountData?.city) {
        payload = {
          ...payload,
          city: userData?.data?.accountData?.city ?? "",
        };
      }
      if (userData?.data?.accountData?.postal_code) {
        payload = {
          ...payload,
          postal_code: userData?.data?.accountData?.postal_code ?? "",
        };
      }
      if (userData?.data?.accountData?.state) {
        payload = {
          ...payload,
          state: userData?.data?.accountData?.state ?? "",
        };
      }
      if (userData?.data?.accountData?.address1) {
        payload = {
          ...payload,
          line1: userData?.data?.accountData?.address1 ?? "",
        };
      }
      if (userData?.data?.accountData?.address2) {
        payload = {
          ...payload,
          line2: userData?.data?.accountData?.address2 ?? "",
        };
      }

      const { error } = await stripe.confirmPayment({
        elements:
          showFinalDisclosure && paymentElement ? paymentElement : elements,
        confirmParams: {
          return_url: stripeBankURL,
          payment_method_data: {
            billing_details: {
              name: userData?.data?.accountData?.first_name,
              email: userData?.data?.accountData?.email,
              address: {
                ...payload,
              },
            },
          },
        },
      });
      if (error) {
        setIsLoading(false);
        console.log("handleSubmit error =====", error);
      }
    };

    const handleSetup = async () => {
      if (!stripe || !elements) {
        return;
      }

      let payload = {};
      if (userData?.data?.accountData?.country) {
        payload = {
          ...payload,
          country: userData?.data?.accountData?.country ?? "US",
        };
      }
      if (userData?.data?.accountData?.city) {
        payload = {
          ...payload,
          city: userData?.data?.accountData?.city ?? "",
        };
      }
      if (userData?.data?.accountData?.postal_code) {
        payload = {
          ...payload,
          postal_code: userData?.data?.accountData?.postal_code ?? "",
        };
      }
      if (userData?.data?.accountData?.state) {
        payload = {
          ...payload,
          state: userData?.data?.accountData?.state ?? "",
        };
      }
      if (userData?.data?.accountData?.address1) {
        payload = {
          ...payload,
          line1: userData?.data?.accountData?.address1 ?? "",
        };
      }
      if (userData?.data?.accountData?.address2) {
        payload = {
          ...payload,
          line2: userData?.data?.accountData?.address2 ?? "",
        };
      }
      const { error } = await stripe.confirmSetup({
        elements:
          showFinalDisclosure && paymentElement ? paymentElement : elements,
        confirmParams: {
          return_url: stripeBankURL,
          payment_method_data: {
            billing_details: {
              name: userData?.data?.accountData?.first_name,
              email: userData?.data?.accountData?.email,
              address: {
                ...payload,
              },
            },
          },
        },
      });
      if (error) {
        setIsLoading(false);
        console.log("handleSubmit error =====", error);
      }
    };

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id,
          payment_method: "ACH",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async () => {
      try {
        let moveAhead = !checkedDuplicatePayment
          ? await checkPastAmount()
          : true;
        if (!moveAhead) {
          setIsLoading(false);
          dispatch(storePaymentElement(elements));
          dispatch(onSetFinalDisclosureLoading(false));
          handleClick();
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            let element = elements ? elements : paymentElement;
            dispatch(storePaymentElement(element));
            setIsLoading(false);
            dispatch(resetDuplicateOrder());
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(onSetShowDisclosure(true));
            handleClick();
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleSubmit = async () => {
      setIsLoading(true);
      let moveAhead = await handleCheckDisclosureAndDuplicate();
      if (!moveAhead) {
        setIsLoading(false);
        return;
      }
      try {
        if (isRecurring) {
          handleSetup();
        } else {
          handlePayment();
        }
      } catch (error) {
        setIsLoading(false);
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        console.log("handleSubmit error =====", error);
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handleSubmit(),
    }));

    return (
      <Button
        text={buttonText}
        {...testAttrs("bank-payment-submit", buttonText)}
        onClick={handleSubmit}
        className={className}
        isLoading={isLoading}
        isDisabled={isDisabled}
        variant="variable"
      />
    );
  }
);

export default BankPaymentButton;
